import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { usePageSeo } from "@/lib/seo";
import { Reveal } from "@/components/site/Motion";
import { FeaturedVideo, videoThumb } from "@/components/site/Sections";
import { VideoModal } from "@/components/site/VideoModal";
import { PageHero } from "./Gallery";

const FILTERS = ["all", "documentary", "wildlife", "travel"];

export default function Video() {
  const { t, L } = useLang();
  usePageSeo("video");
  const { data } = useFetch("/content/videos");
  const [f, setF] = useState("all");
  const [open, setOpen] = useState(null);
  const vids = Array.isArray(data) ? data : [];
  const list = vids.filter((v) => !v.featured && (f === "all" || v.category === f));
  const hero = vids.find((v) => v.featured)?.thumbnail_photo;
  return (
    <div data-testid="video-page">
      <PageHero testid="video-hero" kicker="Films" title={t("video")} lead={t("videoLead")} photo={hero} />
      <FeaturedVideo onPlay={setOpen} />
      <section className="mx-auto max-w-[1600px] px-5 pb-28 md:px-10">
        <div className="mb-10 flex flex-wrap gap-2">
          {FILTERS.map((c) => (
            <button key={c} onClick={() => setF(c)} data-testid={`video-filter-${c}`}
              className={`rounded-full border px-4 py-2 text-xs transition-colors ${f === c ? "border-white bg-white text-black" : "border-white/20 text-white/75 hover:border-white/60"}`}>{c === "all" ? t("all") : t(c === "documentary" ? "documentary" : c)}</button>
          ))}
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          {list.map((v, i) => (
            <Reveal key={v.id} delay={(i % 2) * 0.1}>
              <button onClick={() => setOpen(v)} data-testid={`video-card-${i}`} className="group block w-full text-left">
                <div className="relative aspect-video overflow-hidden bg-[#141414]">
                  {videoThumb(v) && <img src={videoThumb(v)} alt={L(v, "title")} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />}
                  <div className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/5" />
                  <span className="absolute bottom-5 left-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/70 bg-black/30 backdrop-blur-md transition-transform duration-500 group-hover:scale-110"><Play size={18} fill="white" className="ml-0.5 text-white" /></span>
                  {v.duration && <span className="absolute bottom-5 right-5 font-mono text-xs text-white">{v.duration}</span>}
                </div>
                <div className="kicker mt-5 !text-white/55">{t(v.category === "documentary" ? "documentary" : v.category)}</div>
                <h3 className="font-display mt-2 text-2xl font-bold text-white">{L(v, "title")}</h3>
                <p className="mt-2 text-sm text-white/60">{L(v, "description")}</p>
              </button>
            </Reveal>
          ))}
        </div>
      </section>
      <AnimatePresence>{open && <VideoModal video={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </div>
  );
}
